import { useState, useCallback } from "react";
import {
  CalculatePriceDto,
  PhotoSizeItemDto,
  PhotoSizeItemExtraDto,
  ProductItemDto,
  ProductItemExtraDto,
} from "../services/generated-api";
import {
  PricingCalculationResponse,
  PricingCalculationConfig,
} from "../types/pricing";
import { getCurrencyBasedOnDomain } from "../utils/domain.util";
import { getConfiguration, pricingApi } from "../services/api";

/** Local shape of an in-progress photo group passed to the pricing transform. */
type PhotoGroupInput = {
  photoSizeId: string;
  price?: number;
  discount?: number;
  files: Array<{ count?: number }>;
};

/**
 * Hook for calculating prices using the server-side pricing API
 * Follows Azure best practices for error handling and service reliability
 */
export const usePricingCalculation = () => {
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Calculate pricing using the server API
   * Implements error handling and retry logic as per Azure best practices
   */
  const calculatePricing = useCallback(
    async (
      photoSizeItems: PhotoSizeItemExtraDto[],
      productItems: ProductItemExtraDto[],
      config: PricingCalculationConfig = {}
    ): Promise<PricingCalculationResponse | null> => {
      setIsCalculating(true);
      setError(null);
      console.log("Starting pricing calculation...");
      const productItemsData = productItems.map((item) => {
        return {
          productId: item.productId,
          nOfProducts: item.nOfProducts,
          discount: item.discount || 0,
        };
      });
      try {
        // Prepare calculation data with defaults
        const calculationData: CalculatePriceDto = {
          photoSizeItems,
          productItems: productItemsData,
          currency: config.currency || getCurrencyBasedOnDomain(),
          shippingMethod: config.shippingMethod || "normal",
          paymentOption: config.paymentOption || "NORMAL",
          deliveryType: config.deliveryType || "HOME",
          ...(config.couponId && { couponId: config.couponId }),
        };
        console.log("Calculating pricing with data:", calculationData);
        // Call the pricing API
        // Note: The current API returns void, but it should return PricingCalculationResponse
        // This will need to be updated on the backend to return the actual pricing data
        const response = await pricingApi.pricingControllerCalculatePrice(
          calculationData
        );
        console.log("Pricing calculation response:", response);

        // Temporary fallback - return null until backend is updated
        return response.data as unknown as PricingCalculationResponse;
      } catch (err: unknown) {
        console.error("Pricing calculation failed:", err);
        // Comprehensive error handling as per Azure best practices
        const e = err as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        const errorMessage =
          e?.response?.data?.message ||
          e?.message ||
          "Failed to calculate pricing";
        setError(errorMessage);

        // Log error for monitoring and debugging
        console.error("Pricing calculation failed:", {
          error: err,
          photoSizeItems,
          productItems,
          config,
        });

        return null;
      } finally {
        setIsCalculating(false);
      }
    },
    [pricingApi]
  );

  /**
   * Helper function to transform photo groups to API format
   */
  const transformPhotoGroupsToApiFormat = useCallback(
    (photoGroups: PhotoGroupInput[]): PhotoSizeItemExtraDto[] => {
      return photoGroups.map((group) => ({
        photoSizeId: group.photoSizeId,
        price: group.price || 0,
        discount: group.discount || 0,
        nOfPhotos: group.files.reduce(
          (total: number, file: { count?: number }) =>
            total + (file.count || 1),
          0
        ),
      }));
    },
    []
  );

  /**
   * Helper function to transform product quantities to API format
   */
  const transformProductsToApiFormat = useCallback(
    (quantities: Record<string, number>): ProductItemExtraDto[] => {
      return Object.entries(quantities)
        .filter(([, quantity]) => quantity > 0)
        .map(([productId, quantity]) => ({
          productId,
          nOfProducts: quantity,
          price: 0, // Price will be updated after calculation
          discount: 0, // Discount will be updated after calculation
        }));
    },
    []
  );

  return {
    calculatePricing,
    transformPhotoGroupsToApiFormat,
    transformProductsToApiFormat,
    isCalculating,
    error,
  };
};
